import { useNavigate } from 'react-router-dom';

import { useQueryClient } from '@tanstack/react-query';

import { createComment, deleteComment, updateComment } from 'api/Comment';

import { useCustomMutation } from 'lib/hooks/useCustomMutation';

export const useReplies = (commentId, category, refId) => {
  const navigate = useNavigate();

  const queryClient = useQueryClient();

  const handleSuccess = () => {
    try {
      queryClient.invalidateQueries({ queryKey: ['comment-replies', commentId] });
      queryClient.invalidateQueries({ queryKey: ['activities', category, refId] });
    } catch (error) {
      console.error('Error invalidating replies query', error);
    }
  };

  const { mutateAsync: createReplyHandler, isLoading: isCreatingReply } = useCustomMutation({
    mutationFn: (params) => createComment(navigate, { ...params, type: 'reply' }),
    successMessage: 'Reply posted successfully!',
    errorMessage: 'Failed to post reply. Please try again.',
    onSuccess: handleSuccess
  });

  const { mutateAsync: deleteReplyHandler } = useCustomMutation({
    mutationFn: (params) => deleteComment(navigate, { ...params, type: 'reply' }),
    successMessage: 'Reply deleted successfully!',
    errorMessage: 'Failed to delete reply. Please try again.',
    onSuccess: handleSuccess
  });

  const { mutateAsync: updateReplyHandler } = useCustomMutation({
    mutationFn: (params) => updateComment(navigate, { ...params, type: 'reply' }),
    successMessage: 'Reply updated successfully!',
    errorMessage: 'Failed to update reply. Please try again.',
    onSuccess: handleSuccess
  });

  const onReplyPostHandler = async (value) => {
    await createReplyHandler({
      refId,
      refTable: category,
      value,
      parentCommentId: commentId
    });
  };

  const onReplyDeleteHandler = async (replyId) => {
    await deleteReplyHandler({ id: replyId });
  };

  const onReplySaveHandler = async (reply) => {
    await updateReplyHandler({ id: reply.id, value: reply.value });
  };

  return {
    isCreatingReply,
    onReplyPostHandler,
    onReplyDeleteHandler,
    onReplySaveHandler
  };
};
